import { CityPersonRO } from '@routellers/types/dist/city-person/city-person.ro';
import { CreateCityPersonDto } from '@routellers/types/dist/city-person/create-city-person.dto';
import { ModerateCityPersonDto } from '@routellers/types/dist/city-person/moderate-city-person.dto';
import { UpdateCityPersonDto } from '@routellers/types/dist/city-person/update-city-person.dto';
import {
    GeneratePersonContentDto,
    UpsertCityPersonLocaleDto,
} from '@routellers/types/dist/city-person/upsert-city-person-locale.dto';
import {
    AutolinkDto,
    WikidataAutolinkReportRO,
} from '@routellers/types/dist/city-person/wikidata-autolink';
import { GenerateAiCoverDto } from '@routellers/types/dist/shared/ai-cover';
import {
    ContentBatchReportRO,
    GenerateContentBatchDto,
} from '@routellers/types/dist/shared/content-batch';
import {
    DeepenEntityDto,
    DeepenReportRO,
} from '@routellers/types/dist/shared/editorial-research';
import {
    FillCompleteDto,
    FillCompleteReportRO,
} from '@routellers/types/dist/shared/fill-complete';
import { QueryDto } from '@routellers/types/dist/shared/query.dto';
import { QueryRO } from '@routellers/types/dist/shared/query.ro';
import {
    EnrichWikidataDto,
    WikidataEnrichmentReportRO,
} from '@routellers/types/dist/shared/wikidata-enrichment';

import { ApiMethodDeclaration } from '..';

const query: ApiMethodDeclaration<QueryDto, QueryRO<CityPersonRO>> = (params) => ({
    url: '/admin/city-people',
    method: 'GET',
    params,
});

const getOne: ApiMethodDeclaration<number, CityPersonRO> = (id) => ({
    url: `/admin/city-people/${id}`,
    method: 'GET',
});

const create: ApiMethodDeclaration<{ data: CreateCityPersonDto }, CityPersonRO> = ({ data }) => ({
    url: '/admin/city-people',
    method: 'POST',
    data,
});

const update: ApiMethodDeclaration<{ id: number; data: UpdateCityPersonDto }, CityPersonRO> = ({
    id,
    data,
}) => ({
    url: `/admin/city-people/${id}`,
    method: 'PATCH',
    data,
});

const generateContent: ApiMethodDeclaration<
    { id: number; data: GeneratePersonContentDto },
    CityPersonRO
> = ({ id, data }) => ({
    url: `/admin/city-people/${id}/generate-content`,
    method: 'POST',
    data,
});

const upsertLocale: ApiMethodDeclaration<
    { id: number; data: UpsertCityPersonLocaleDto },
    CityPersonRO
> = ({ id, data }) => ({
    url: `/admin/city-people/${id}/locales`,
    method: 'PATCH',
    data,
});

const enrichWikidata: ApiMethodDeclaration<
    { data: EnrichWikidataDto },
    WikidataEnrichmentReportRO
> = ({ data }) => ({
    url: '/admin/city-people/maintenance/enrich-wikidata',
    method: 'POST',
    data,
});

const autolink: ApiMethodDeclaration<{ data: AutolinkDto }, WikidataAutolinkReportRO> = ({
    data,
}) => ({
    url: '/admin/city-people/maintenance/autolink',
    method: 'POST',
    data,
});

const generateContentBatch: ApiMethodDeclaration<
    { data: GenerateContentBatchDto },
    ContentBatchReportRO
> = ({ data }) => ({
    url: '/admin/city-people/maintenance/generate-content-batch',
    method: 'POST',
    data,
});

const generateCover: ApiMethodDeclaration<
    { id: number; data: GenerateAiCoverDto },
    CityPersonRO
> = ({ id, data }) => ({
    url: `/admin/city-people/${id}/generate-cover`,
    method: 'POST',
    data,
});

const deepen: ApiMethodDeclaration<
    { id: number; data: DeepenEntityDto },
    DeepenReportRO<CityPersonRO>
> = ({ id, data }) => ({
    url: `/admin/city-people/${id}/deepen`,
    method: 'POST',
    data,
});

const fillComplete: ApiMethodDeclaration<
    { id: number; data: FillCompleteDto },
    FillCompleteReportRO<CityPersonRO>
> = ({ id, data }) => ({
    url: `/admin/city-people/${id}/fill-complete`,
    method: 'POST',
    data,
});

const remove: ApiMethodDeclaration<number, { success: boolean }> = (id) => ({
    url: `/admin/city-people/${id}`,
    method: 'DELETE',
});

const archive: ApiMethodDeclaration<{ id: number; data: { archived?: boolean } }, CityPersonRO> = ({
    id,
    data,
}) => ({
    url: `/admin/city-people/${id}/archive`,
    method: 'POST',
    data,
});

const moderate: ApiMethodDeclaration<{ id: number; data: ModerateCityPersonDto }, CityPersonRO> = ({
    id,
    data,
}) => ({
    url: `/admin/city-people/${id}/moderate`,
    method: 'POST',
    data,
});

export const cityPeopleApiDeclaration = {
    query,
    getOne,
    create,
    update,
    generateContent,
    upsertLocale,
    enrichWikidata,
    autolink,
    generateContentBatch,
    generateCover,
    deepen,
    fillComplete,
    remove,
    archive,
    moderate,
};
